/**
 * IDG Insights Email Settings Component
 * 
 * Configuration UI for automated weekly IDG meeting analytics emails.
 * Admin-only feature for setting up email recipients, schedule, and content.
 */

import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import {
  Mail, Clock, Users, Send, Settings, Eye, History,
  Plus, X, Calendar, CheckCircle, AlertCircle, Loader2,
  BarChart3, MessageSquare, ListChecks, UserCheck, FileText
} from "lucide-react";
import api from "@/lib/api";

const FREQUENCY_OPTIONS = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Bi-weekly" },
  { value: "monthly", label: "Monthly" }
];

const DAY_OPTIONS = [
  "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"
];

const SECTION_OPTIONS = [
  { id: "summary", label: "Summary Metrics", icon: BarChart3, description: "Total meetings, duration, participants" },
  { id: "speaker_participation", label: "Speaker Participation", icon: Users, description: "Attendance by role" },
  { id: "topics", label: "Discussion Topics", icon: MessageSquare, description: "Most discussed topics" },
  { id: "action_items", label: "Action Items", icon: ListChecks, description: "Completion status" },
  { id: "patients_discussed", label: "Patients Discussed", icon: UserCheck, description: "Most discussed patients" }
];

export default function IDGInsightsEmailSettings({ onClose }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [activeTab, setActiveTab] = useState("settings");
  
  // Configuration state
  const [enabled, setEnabled] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [newRecipient, setNewRecipient] = useState("");
  const [ccRecipients, setCcRecipients] = useState([]);
  const [newCcRecipient, setNewCcRecipient] = useState("");
  const [frequency, setFrequency] = useState("weekly");
  const [sendDay, setSendDay] = useState("Monday");
  const [sendTime, setSendTime] = useState("08:00");
  const [includeSections, setIncludeSections] = useState([
    "summary", "speaker_participation", "topics", "action_items", "patients_discussed"
  ]);
  
  // Status info
  const [lastSent, setLastSent] = useState(null);
  const [nextScheduled, setNextScheduled] = useState(null);
  const [status, setStatus] = useState("not_configured");
  
  // Preview and history
  const [previewHtml, setPreviewHtml] = useState("");
  const [emailHistory, setEmailHistory] = useState([]);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const response = await api.get("/idg-insights/config");
      const data = response.data;
      
      if (data.config) {
        setEnabled(data.config.enabled || false);
        setRecipients(data.config.recipients || []);
        setCcRecipients(data.config.cc_recipients || []);
        setFrequency(data.config.frequency || "weekly");
        setSendDay(data.config.send_day || "Monday");
        setSendTime(data.config.send_time || "08:00");
        setIncludeSections(data.config.include_sections || ["summary", "speaker_participation", "topics", "action_items", "patients_discussed"]);
      }
      
      setLastSent(data.last_sent);
      setNextScheduled(data.next_scheduled);
      setStatus(data.status || "not_configured");
    } catch (err) {
      console.log("Using default config");
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    if (enabled && recipients.length === 0) {
      toast.error("Please add at least one recipient");
      return;
    }
    
    setSaving(true);
    try {
      await api.post("/idg-insights/config", {
        enabled,
        recipients,
        cc_recipients: ccRecipients,
        frequency,
        send_day: sendDay,
        send_time: sendTime,
        include_sections: includeSections
      });
      
      toast.success("Email settings saved successfully");
      loadConfig();
    } catch (err) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const sendNow = async () => {
    if (recipients.length === 0) {
      toast.error("Please add at least one recipient");
      return;
    }
    
    setSending(true);
    try {
      const response = await api.post("/idg-insights/send-now");
      toast.success(response.data.message);
      if (response.data.note) {
        toast.info(response.data.note, { duration: 5000 });
      }
      loadConfig();
    } catch (err) {
      toast.error("Failed to send email");
    } finally {
      setSending(false);
    }
  };

  const loadPreview = async () => {
    try {
      const response = await api.get("/idg-insights/preview");
      setPreviewHtml(response.data.html);
    } catch (err) {
      toast.error("Failed to load preview");
    }
  };

  const loadHistory = async () => {
    try {
      const response = await api.get("/idg-insights/history");
      setEmailHistory(response.data.emails || []);
    } catch (err) {
      console.log("Failed to load history");
    }
  };

  const addRecipient = () => {
    if (!newRecipient.trim()) return;
    if (!newRecipient.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }
    if (recipients.includes(newRecipient.trim())) {
      toast.error("Email already added");
      return;
    }
    setRecipients([...recipients, newRecipient.trim()]);
    setNewRecipient("");
  };

  const removeRecipient = (email) => {
    setRecipients(recipients.filter(r => r !== email));
  };

  const addCcRecipient = () => {
    if (!newCcRecipient.trim()) return;
    if (!newCcRecipient.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }
    if (ccRecipients.includes(newCcRecipient.trim())) {
      toast.error("Email already added");
      return;
    }
    setCcRecipients([...ccRecipients, newCcRecipient.trim()]);
    setNewCcRecipient("");
  };

  const removeCcRecipient = (email) => {
    setCcRecipients(ccRecipients.filter(r => r !== email));
  };

  const toggleSection = (sectionId) => {
    if (includeSections.includes(sectionId)) {
      setIncludeSections(includeSections.filter(s => s !== sectionId));
    } else {
      setIncludeSections([...includeSections, sectionId]);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-teal-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="idg-insights-email-settings">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <Mail className="w-6 h-6 text-teal-400" />
            IDG Insights Email
          </h2>
          <p className="text-slate-400 text-sm">Automated weekly meeting analytics reports</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Switch
              checked={enabled}
              onCheckedChange={setEnabled}
              id="email-enabled"
            />
            <Label htmlFor="email-enabled" className="text-white">
              {enabled ? "Enabled" : "Disabled"}
            </Label>
          </div>
        </div>
      </div>

      {/* Status Banner */}
      <div className={`px-4 py-3 rounded-lg flex items-center justify-between ${
        status === "active" ? "bg-emerald-900/30 border border-emerald-500/50" :
        status === "disabled" ? "bg-slate-800 border border-slate-600" :
        "bg-amber-900/20 border border-amber-500/30"
      }`}>
        <div className="flex items-center gap-3">
          {status === "active" ? (
            <CheckCircle className="w-5 h-5 text-emerald-400" />
          ) : (
            <AlertCircle className="w-5 h-5 text-amber-400" />
          )}
          <div>
            <p className="text-white text-sm font-medium">
              {status === "active" ? "Email reports are active" : 
               status === "disabled" ? "Email reports are disabled" :
               "Email reports not configured"}
            </p>
            {nextScheduled && status === "active" && (
              <p className="text-slate-400 text-xs">
                Next send: {new Date(nextScheduled).toLocaleString()}
              </p>
            )}
          </div>
        </div>
        {lastSent && (
          <Badge variant="outline" className="text-slate-400 border-slate-600">
            Last sent: {new Date(lastSent).toLocaleDateString()}
          </Badge>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(val) => {
        setActiveTab(val);
        if (val === "preview") loadPreview();
        if (val === "history") loadHistory();
      }}>
        <TabsList className="bg-slate-800 border border-slate-700">
          <TabsTrigger value="settings" className="flex items-center gap-1">
            <Settings className="w-4 h-4" />
            Settings
          </TabsTrigger>
          <TabsTrigger value="preview" className="flex items-center gap-1">
            <Eye className="w-4 h-4" />
            Preview
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-1">
            <History className="w-4 h-4" />
            History
          </TabsTrigger>
        </TabsList>

        <TabsContent value="settings" className="mt-4 space-y-4">
          {/* Recipients Section */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-sm flex items-center gap-2">
                <Users className="w-4 h-4 text-blue-400" />
                Recipients
              </CardTitle>
              <CardDescription className="text-slate-400">
                Add email addresses to receive the weekly report
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Main Recipients */}
              <div>
                <Label className="text-slate-300 text-sm">To:</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    value={newRecipient}
                    onChange={(e) => setNewRecipient(e.target.value)}
                    placeholder="email@example.com"
                    className="bg-slate-700 border-slate-600"
                    onKeyDown={(e) => e.key === "Enter" && addRecipient()}
                  />
                  <Button onClick={addRecipient} variant="outline" size="icon">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {recipients.map(email => (
                    <Badge key={email} variant="secondary" className="bg-slate-700 text-white">
                      {email}
                      <button onClick={() => removeRecipient(email)} className="ml-2 hover:text-red-400">
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                  {recipients.length === 0 && (
                    <span className="text-slate-500 text-sm">No recipients added</span>
                  )}
                </div>
              </div>

              <Separator className="bg-slate-700" />

              {/* CC Recipients */}
              <div>
                <Label className="text-slate-300 text-sm">CC (Optional):</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    value={newCcRecipient}
                    onChange={(e) => setNewCcRecipient(e.target.value)}
                    placeholder="cc@example.com"
                    className="bg-slate-700 border-slate-600"
                    onKeyDown={(e) => e.key === "Enter" && addCcRecipient()}
                  />
                  <Button onClick={addCcRecipient} variant="outline" size="icon">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {ccRecipients.map(email => (
                    <Badge key={email} variant="outline" className="border-slate-600 text-slate-300">
                      {email}
                      <button onClick={() => removeCcRecipient(email)} className="ml-2 hover:text-red-400">
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Schedule Section */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-sm flex items-center gap-2">
                <Clock className="w-4 h-4 text-amber-400" />
                Schedule
              </CardTitle>
              <CardDescription className="text-slate-400">
                Configure when to send the report
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-slate-300 text-sm">Frequency</Label>
                  <Select value={frequency} onValueChange={setFrequency}>
                    <SelectTrigger className="bg-slate-700 border-slate-600 mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FREQUENCY_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {(frequency === "weekly" || frequency === "biweekly") && (
                  <div>
                    <Label className="text-slate-300 text-sm">Day</Label>
                    <Select value={sendDay} onValueChange={setSendDay}>
                      <SelectTrigger className="bg-slate-700 border-slate-600 mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DAY_OPTIONS.map(day => (
                          <SelectItem key={day} value={day}>
                            {day}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                <div>
                  <Label className="text-slate-300 text-sm">Time (UTC)</Label>
                  <Input
                    type="time"
                    value={sendTime}
                    onChange={(e) => setSendTime(e.target.value)}
                    className="bg-slate-700 border-slate-600 mt-1"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Content Sections */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-sm flex items-center gap-2">
                <FileText className="w-4 h-4 text-purple-400" />
                Email Content
              </CardTitle>
              <CardDescription className="text-slate-400">
                Select which sections to include in the report
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {SECTION_OPTIONS.map(section => (
                  <div 
                    key={section.id}
                    className={`p-3 rounded-lg cursor-pointer transition-all flex items-center gap-3 ${
                      includeSections.includes(section.id)
                        ? "bg-teal-900/30 border border-teal-500/50"
                        : "bg-slate-800 hover:bg-slate-700 border border-transparent"
                    }`}
                    onClick={() => toggleSection(section.id)}
                  >
                    <Checkbox 
                      checked={includeSections.includes(section.id)}
                      onCheckedChange={() => toggleSection(section.id)}
                    />
                    <section.icon className={`w-5 h-5 ${
                      includeSections.includes(section.id) ? "text-teal-400" : "text-slate-500"
                    }`} />
                    <div>
                      <p className="text-white font-medium">{section.label}</p>
                      <p className="text-slate-400 text-xs">{section.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex items-center justify-between pt-2">
            <Button
              variant="outline"
              onClick={sendNow}
              disabled={sending || recipients.length === 0}
              className="border-teal-500 text-teal-400 hover:bg-teal-900/30"
            >
              {sending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Send Now
            </Button>
            <Button onClick={saveConfig} disabled={saving}>
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              Save Settings
            </Button>
          </div>

          {/* Mocked Notice */}
          <div className="p-3 bg-amber-900/20 border border-amber-500/30 rounded-lg">
            <p className="text-amber-300 text-sm flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              <span><strong>Note:</strong> Email sending is MOCKED until Resend API key is configured.</span>
            </p>
          </div>
        </TabsContent>

        <TabsContent value="preview" className="mt-4">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-sm">Email Preview</CardTitle>
            </CardHeader>
            <CardContent>
              {previewHtml ? (
                <div 
                  className="bg-white rounded-lg overflow-hidden"
                  style={{ maxHeight: "500px", overflow: "auto" }}
                  dangerouslySetInnerHTML={{ __html: previewHtml }}
                />
              ) : (
                <div className="flex items-center justify-center h-64 text-slate-400">
                  <Loader2 className="w-6 h-6 animate-spin mr-2" />
                  Loading preview...
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-sm">Email History</CardTitle>
            </CardHeader>
            <CardContent>
              {emailHistory.length > 0 ? (
                <ScrollArea className="h-[300px]">
                  <div className="space-y-2">
                    {emailHistory.map(email => (
                      <div key={email.id} className="p-3 bg-slate-800 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-white font-medium">{email.subject}</p>
                            <p className="text-slate-400 text-xs">
                              To: {email.recipients?.join(", ")}
                            </p>
                          </div>
                          <div className="text-right">
                            <Badge variant={email.status === "mocked" ? "outline" : "default"}>
                              {email.status}
                            </Badge>
                            <p className="text-slate-500 text-xs mt-1">
                              {new Date(email.sent_at).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              ) : (
                <div className="flex items-center justify-center h-32 text-slate-400">
                  No emails sent yet
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export { IDGInsightsEmailSettings };
